/**
 * Cloudflare Turnstile placement rules, shared by every form that talks to
 * Supabase Auth. Supabase only enforces the token once CAPTCHA protection is
 * switched on (Authentication → Attack Protection); until then a missing token
 * is accepted, so the widget can ship before enforcement.
 */

import { APP_HOST } from "@/lib/site/hosts";

/** Previews live under pr-<N>.zettlapp.com, the hosts Turnstile accepts. */
export const PREVIEW_DOMAIN = "zettlapp.com";

export interface BuildInfo {
  /** VERCEL_ENV at build time: "production", "preview", or "" off Vercel. */
  vercelEnv: string;
  /** VERCEL_GIT_PULL_REQUEST_ID at build time; empty on a PR's first build. */
  pullRequestId: string;
}

type PageLocation = Pick<Location, "hostname" | "pathname" | "search" | "hash">;

/**
 * "widget" — render Turnstile and require a token before submitting.
 * "hint"   — Turnstile never accepts *.vercel.app, so point at an address it does.
 * "off"    — no site key configured: no widget, forms submit without a token.
 */
export type CaptchaMode =
  { kind: "widget"; siteKey: string } | { kind: "hint"; href: string | null } | { kind: "off" };

export function captchaMode(
  siteKey: string | undefined,
  location: PageLocation,
  build: BuildInfo
): CaptchaMode {
  if (!siteKey) return { kind: "off" };
  if (!location.hostname.endsWith(".vercel.app")) return { kind: "widget", siteKey };

  const path = `${location.pathname}${location.search}${location.hash}`;
  if (build.vercelEnv === "production") {
    return { kind: "hint", href: `https://${APP_HOST}${path}` };
  }
  if (/^\d+$/.test(build.pullRequestId)) {
    return { kind: "hint", href: `https://pr-${build.pullRequestId}.${PREVIEW_DOMAIN}${path}` };
  }
  return { kind: "hint", href: null };
}

export function currentCaptchaMode(): CaptchaMode {
  return captchaMode(import.meta.env.VITE_TURNSTILE_SITE_KEY, window.location, {
    vercelEnv: import.meta.env.VITE_VERCEL_ENV ?? "",
    pullRequestId: import.meta.env.VITE_VERCEL_GIT_PULL_REQUEST_ID ?? "",
  });
}
